import { type KeyboardEvent, type ReactNode, useId, useRef, useState } from 'react'
import { cn } from '../cn'

export interface TabItem {
  id: string
  label: ReactNode
  content: ReactNode
  /** Small count or badge after the label. */
  count?: number
  disabled?: boolean
}

export interface TabsProps {
  /** Accessible name of the tab list ("Company sections"). */
  label: string
  items: TabItem[]
  /** Controlled selection. */
  value?: string
  defaultValue?: string
  onValueChange?: (id: string) => void
  className?: string
}

/**
 * WAI-ARIA tabs (UI-050): roving tabindex, arrow keys / Home / End, automatic activation.
 * Controlled or uncontrolled. Give it a `key` to reset the selection (UI-014: per company).
 */
export function Tabs({ label, items, value, defaultValue, onValueChange, className }: TabsProps) {
  const baseId = useId()
  const [internal, setInternal] = useState(defaultValue ?? items[0]?.id)
  const selected = value ?? internal
  const tabRefs = useRef(new Map<string, HTMLButtonElement>())
  const enabled = items.filter((item) => !item.disabled)

  function select(id: string) {
    if (value === undefined) setInternal(id)
    onValueChange?.(id)
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = enabled.findIndex((item) => item.id === selected)
    let next: TabItem | undefined
    if (event.key === 'ArrowRight') next = enabled[(index + 1) % enabled.length]
    else if (event.key === 'ArrowLeft')
      next = enabled[(index - 1 + enabled.length) % enabled.length]
    else if (event.key === 'Home') next = enabled[0]
    else if (event.key === 'End') next = enabled[enabled.length - 1]
    if (!next) return
    event.preventDefault()
    select(next.id)
    tabRefs.current.get(next.id)?.focus()
  }

  const active = items.find((item) => item.id === selected)

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="-mx-1 mb-6 flex gap-6 overflow-x-auto border-b border-line px-1"
      >
        {items.map((item) => {
          const isSelected = item.id === selected
          return (
            <button
              key={item.id}
              ref={(node) => {
                if (node) tabRefs.current.set(item.id, node)
                else tabRefs.current.delete(item.id)
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${item.id}`}
              aria-selected={isSelected}
              aria-controls={`${baseId}-panel-${item.id}`}
              tabIndex={isSelected ? 0 : -1}
              disabled={item.disabled}
              onClick={() => select(item.id)}
              className={cn(
                '-mb-px flex shrink-0 items-center gap-2 border-b-[3px] px-0.5 pt-1 pb-3 text-sm whitespace-nowrap transition-colors disabled:opacity-50',
                isSelected
                  ? 'border-teal font-semibold text-teal'
                  : 'border-transparent text-sage hover:border-line-strong hover:text-ink',
              )}
            >
              {item.label}
              {item.count !== undefined ? (
                <span
                  className={cn(
                    'rounded-full px-1.5 text-xs tabular-nums leading-5',
                    isSelected ? 'bg-teal-50 text-teal' : 'bg-line-soft text-sage',
                  )}
                >
                  {item.count}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
      {active ? (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${active.id}`}
          aria-labelledby={`${baseId}-tab-${active.id}`}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: WAI-ARIA tabs pattern makes the panel focusable
          tabIndex={0}
          className="focus-visible:outline-offset-4"
        >
          {active.content}
        </div>
      ) : null}
    </div>
  )
}
